// Object.hasOwn (ES2022) is polyfilled in index.html, not here — see that
// file for why. A version of this polyfill was tried here first, but Terser
// silently eliminated it from the production bundle (confirmed via a
// byte-level diff: present pre-minification, gone after minify), so it was
// providing no actual protection. An inline <script> in index.html runs
// before any bundled JS and isn't processed by the JS minifier at all.

import "@/lib/logger"; // Initialize logger first to patch console
import { initSentry } from "@/lib/sentry";
initSentry(); // Idempotent: the module also initializes on import.
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { registerSW } from "virtual:pwa-register";
import { isStreamActive } from "@/lib/aiStream";


// Recover from stale-deploy asset failures. After a redeploy, a client holding
// an old index.html requests old hashed chunks/CSS that no longer exist; Vite
// then throws "Unable to preload CSS for /assets/…" or a dynamic-import error,
// which otherwise white-screens the landing page. Vite fires `vite:preloadError`
// for exactly these cases — do a single hard reload to pull the fresh index.html
// and assets. The sessionStorage guard prevents a reload loop if the asset is
// genuinely, persistently missing (a real 404, not just a stale hash).
//
// Two things this MUST do that a naive listener misses:
// 1. event.preventDefault() — Vite's preload helper re-throws the original
//    error immediately after dispatching this event unless a listener calls
//    preventDefault(), regardless of what else the listener does. Without it,
//    the thrown error still propagates to the nearest error boundary and
//    renders "Something went wrong" — which can win a race against reload()
//    actually navigating away, or persist for good if the reload below is
//    skipped by the guard.
// 2. Unregister any service worker before reloading — if a stale SW is still
//    serving a precached old app shell, a plain reload() just re-fetches the
//    exact same broken page from the SW's cache instead of the network,
//    hitting this same error again, tripping the guard, and getting stuck on
//    the error boundary permanently instead of self-healing.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();

  let alreadyReloaded = false;
  try {
    alreadyReloaded = window.sessionStorage.getItem("preload-error-reloaded") === "1";
    window.sessionStorage.setItem("preload-error-reloaded", "1");
  } catch {
    // sessionStorage unavailable (private mode) — treat as not-yet-reloaded.
  }
  if (alreadyReloaded) return;

  const doReload = async () => {
    try {
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
    } catch {
      // Best-effort — still reload even if this fails.
    } finally {
      window.location.reload();
    }
  };

  // A hard reload mid-stream silently discards whatever the AI was in the
  // middle of answering (chat, quiz gen, etc. all flow through the same SSE
  // helper) with no error, no save, nothing — exactly what was reported as
  // "the page reloaded and the conversation was just gone". Defer the reload
  // until the stream settles instead of yanking the tab out from under it.
  // Capped at 30s so a stream that never resolves can't block recovery from
  // a genuinely stale deploy forever.
  if (isStreamActive()) {
    const start = Date.now();
    const waitForStreamToSettle = setInterval(() => {
      if (!isStreamActive() || Date.now() - start > 30000) {
        clearInterval(waitForStreamToSettle);
        doReload();
      }
    }, 1000);
    return;
  }

  doReload();
});

// Register Service Worker for PWA
registerSW({
  onNeedRefresh() {
    console.log("[System] Update available. Please refresh to apply changes.");
  },
  onOfflineReady() {
    console.log("[System] Offline mode initialized successfully.");
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
